import * as THREE from 'three';
import { Line2 } from 'three/addons/lines/Line2.js';
import { LineGeometry } from 'three/addons/lines/LineGeometry.js';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import { BaseObject } from './base';

export class Measurement extends BaseObject {
  constructor(properties = {}) {
    super();
    const points = properties.data.points || [];
    const group = new THREE.Group();

    // Create line material
    const lineMaterial = new LineMaterial({
      color: 0xffffff,
      linewidth: 2,
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
      depthTest: false,
      depthWrite: false,
      transparent: true,
      opacity: 1.0,
    });

    // Create line geometry
    const lineGeometry = new LineGeometry();
    const line = new Line2(lineGeometry, lineMaterial);
    line.renderOrder = 1000;
    line.frustumCulled = false;
    // Don't add line to group yet - will add it when points are set

    // Create distance label element
    const distanceLabel = document.createElement('div');
    distanceLabel.classList.add('text-white', 'text-sm', 'pointer-events-none');
    distanceLabel.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
    distanceLabel.style.padding = '4px 8px';
    distanceLabel.style.borderRadius = '4px';
    distanceLabel.style.fontSize = '14px';
    distanceLabel.style.fontFamily = 'Arial';

    const distanceLabelObject = new CSS2DObject(distanceLabel);
    group.add(distanceLabelObject);

    // Store references
    group.line = line;
    group.label = distanceLabel;
    group.labelObject = distanceLabelObject;
    group.userData.points = []; // Store points for gizmo access
    group.userData.lineAdded = false; // Track if line has been added to group

    // Add update method
    group.updatePoints = (points) => {
      if (points.length === 0) return;

      // Convert points to Vector3 objects if they aren't already
      const vectorPoints = points.map((point) => {
        if (point instanceof THREE.Vector3) {
          return point;
        }
        return new THREE.Vector3(point.x, point.y, point.z);
      });

      // Store in userData for gizmo access
      group.userData.points = vectorPoints;

      // Update line geometry
      const positions = new Float32Array(vectorPoints.length * 3);
      vectorPoints.forEach((point, i) => {
        positions[i * 3] = point.x;
        positions[i * 3 + 1] = point.y;
        positions[i * 3 + 2] = point.z;
      });
      line.geometry.setPositions(positions);
      line.computeLineDistances();

      // Add line to group if not already added
      if (!group.userData.lineAdded) {
        group.add(line);
        group.userData.lineAdded = true;
      }

      // Update measurement text and position
      if (vectorPoints.length === 2) {
        const distance = vectorPoints[0].distanceTo(vectorPoints[1]);
        const midPoint = new THREE.Vector3().addVectors(vectorPoints[0], vectorPoints[1]).multiplyScalar(0.5);

        distanceLabel.textContent = `${distance.toFixed(2)}m`;
        distanceLabelObject.position.copy(midPoint);
      }
    };

    // Add cleanup method
    const originalRemove = group.remove.bind(group);
    group.remove = () => {
      if (distanceLabel.parentNode) {
        distanceLabel.parentNode.removeChild(distanceLabel);
      }
      line.geometry.dispose();
      line.material.dispose();
      originalRemove();
    };

    group.addEventListener('removed', () => {
      if (distanceLabel.parentNode) {
        distanceLabel.parentNode.removeChild(distanceLabel);
      }
      line.geometry.dispose();
      line.material.dispose();
    });

    // Initialize with points if provided
    if (points.length > 0) {
      group.updatePoints(points);
    }

    group.clearSelection = () => {
      // No visual selection state for annotations yet
    };

    group.select = () => {
      // No visual selection state for annotations yet
    };

    // Add applyProperties method for objectManager
    group.applyProperties = (properties) => {
      if (properties.points) {
        group.updatePoints(properties.points);
      }
    };

    return group;
  }
}
